import { Clarification, type ClarificationAnswers } from "@prelude-ai/design-system"
import { GripVertical } from "lucide-react"
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels"
import type { ToolCall } from "../../types"
import type { PendingClarification } from "../hooks/chat-reducer.ts"
import type { ChatMessage, StagedAttachment, StepInfo } from "../types"
import DevToolsPanel from "./DevToolsPanel"
import InputArea from "./InputArea"
import MessageList from "./MessageList"

export type ChatInterfaceProps = {
  messages?: ChatMessage[]
  currentResponse: string
  isLoading?: boolean
  devToolsMode?: boolean
  stepPath: string[]
  stepHistory: StepInfo[]
  currentToolCall?: ToolCall
  clarification?: PendingClarification | null
  onSubmitClarification: (answers: ClarificationAnswers | null) => void
  onCloseClarification: () => void
  flowState: Record<string, unknown>
  currentThought?: string | null
  onSend?: () => void
  transcriptDownloadUrl?: string
  transcriptDownloadFilename?: string
  onCloseDevTools?: () => void
  setInput: (value: string) => void
  input: string
  attachments?: StagedAttachment[]
  onAttach?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  isUploading?: boolean
  devToolsInitialTab?: "trace" | "state" | "prompts" | "starting-state"
  startingFlowStateEditor?: React.ReactNode
}

export default function ChatInterface({
  input,
  messages = [],
  isLoading = false,
  currentResponse,
  devToolsMode,
  stepPath = [],
  stepHistory,
  currentToolCall,
  clarification,
  // Default to no-ops so the render guard can narrow on `clarification` alone. A pending
  // clarification is only ever passed together with its handlers (routes that don't wire
  // clarification pass none of the three), so these defaults are never actually invoked.
  onSubmitClarification,
  onCloseClarification,
  flowState,
  currentThought,
  onSend,
  transcriptDownloadUrl,
  transcriptDownloadFilename,
  onCloseDevTools,
  setInput,
  attachments,
  onAttach,
  onRemoveAttachment,
  isUploading,
  devToolsInitialTab,
  startingFlowStateEditor
}: ChatInterfaceProps) {
  if (devToolsMode) {
    return (
      <PanelGroup orientation="horizontal" className="flex-1 min-h-0">
        {/* Left Panel - Chat */}
        <Panel className="flex" defaultSize={33} minSize={20}>
          <div className="h-full flex-1 flex flex-col border-r border-border">
            <div className="p-2 border-b border-border bg-muted/30">
              <span className="text-xs font-medium text-muted-foreground">Chat</span>
            </div>
            <MessageList
              className="flex-1"
              messages={messages}
              currentResponse={currentResponse}
              currentToolCall={currentToolCall}
              isLoading={isLoading}
              currentThought={currentThought}
              devMode={!!devToolsMode}
            />
            {clarification && (
              <Clarification
                key={clarification.toolCallId}
                className="mx-4 mb-2"
                questions={clarification.questions.map((question) => question.message)}
                onComplete={onSubmitClarification}
                onClose={onCloseClarification}
              />
            )}
            <InputArea
              className="sm:p-0 md:p-4 pt-0"
              input={input}
              setInput={setInput}
              onSend={onSend}
              isLoading={isLoading}
              attachments={attachments}
              onAttach={onAttach}
              onRemoveAttachment={onRemoveAttachment}
              isUploading={isUploading}
            />
          </div>
        </Panel>

        <PanelResizeHandle className="w-2 bg-border hover:bg-primary/20 transition-colors flex items-center justify-center group">
          <GripVertical className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
        </PanelResizeHandle>

        {/* Right Panel - Tabbed View */}
        <Panel defaultSize={67} minSize={30}>
          <DevToolsPanel
            stepHistory={stepHistory}
            stepPath={stepPath}
            flowState={flowState}
            transcriptDownloadUrl={transcriptDownloadUrl}
            transcriptDownloadFilename={transcriptDownloadFilename}
            onCloseDevTools={onCloseDevTools}
            initialTab={devToolsInitialTab}
            startingFlowStateEditor={startingFlowStateEditor}
          />
        </Panel>
      </PanelGroup>
    )
  }

  // Normal mode: standard chat layout
  return (
    <div className="flex min-h-0 h-full flex-col bg-background">
      <MessageList
        className="flex-1"
        messages={messages}
        currentResponse={currentResponse}
        currentToolCall={currentToolCall}
        isLoading={isLoading}
        currentThought={currentThought}
        devMode={!!devToolsMode}
      />
      <div>
        <div className="mx-auto max-w-4xl">
          {clarification && (
            <Clarification
              key={clarification.toolCallId}
              className="mx-4 mb-2 w-auto"
              questions={clarification.questions.map((question) => question.message)}
              onComplete={onSubmitClarification}
              onClose={onCloseClarification}
            />
          )}
          <InputArea
            className="p-4 pt-0"
            input={input}
            setInput={setInput}
            onSend={onSend}
            isLoading={isLoading}
            attachments={attachments}
            onAttach={onAttach}
            onRemoveAttachment={onRemoveAttachment}
            isUploading={isUploading}
          />
        </div>
      </div>
    </div>
  )
}
